import React from "react";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { formatPhoneNumber } from "../../../globalFunctions/PhoneNumberFormatter";

// -------------------------------
// 1️⃣ GROUPEMENT DES SHIPPING
// -------------------------------
function groupShipping(shippingDetails) {
  if (!shippingDetails) return [];

  return Object.values(
    shippingDetails.reduce((groups, s) => {
      const key = `${s.companyId}|${s.warehouseId}|${s.carrier}|${s.serviceName}`;
      if (!groups[key]) groups[key] = [];
      groups[key].push(s);
      return groups;
    }, {})
  );
}

function findShippingForItem(item, shippingDetails) {
  const productId = item.productId;

  // 1) Shipping correspond aux produits envoyés
  let match = shippingDetails.find((s) => s.productIds?.includes(productId));
  if (match) return match;

  // 2) fallback : shipping sans productIds
  return (
    shippingDetails.find((s) => !s.productIds || s.productIds.length === 0) ||
    null
  );
}

// -------------------------------------------------------
// 2️⃣ ASSOCIE LES PRODUITS À LEUR GROUPE DE SHIPPING
// -------------------------------------------------------
function buildGroups(items, shippingDetails) {
  const groups = {};

  items.forEach((item) => {
    const shipping = findShippingForItem(item, shippingDetails);

    const key = shipping
      ? `shipping-${shipping.companyId}`
      : `no-shipping-${item.orderItemId}`;

    if (!groups[key]) groups[key] = { shipping, items: [] };
    groups[key].items.push(item);
  });

  return Object.values(groups);
}

export default function OrderDetailsModal({ order, onClose }) {
  if (!order) return null;

  const shippingGroups = buildGroups(order.items, order.shippingDetails);

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-start justify-center overflow-y-auto py-10 px-4">
      <div className="bg-base-100 w-full max-w-[80%] rounded-xl shadow-2xl border border-base-300 max-h-[90vh] overflow-y-auto">
        {/* HEADER */}
        <div className="flex items-center justify-between p-5 border-b">
          <h2 className="text-2xl font-bold tracking-wide">
            Commande # {order.orderNumber}
          </h2>
          <button className="btn btn-ghost btn-sm" onClick={onClose}>
            <XMarkIcon className="w-6 h-6" />
          </button>
        </div>

        {/* CONTENT */}
        <div className="p-8">
          {/* =========================== */}
          {/*   INFO PRINCIPALE           */}
          {/* =========================== */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 border-b pb-6 mb-6">
            {/* LEFT */}
            <div className="space-y-2">
              <div className="text-md opacity-90">
                Date :
                <span className="font-bold ml-1">
                  {order.orderDate
                    ? new Date(order.orderDate).toLocaleString()
                    : "—"}
                </span>
              </div>
            </div>

            {/* RIGHT : client */}
            <div className="space-y-2">
              {order?.buyerName && (
                <div className="text-md opacity-90">
                  Nom du client :
                  <span className="font-bold ml-1">{order.buyerName}</span>
                </div>
              )}

              {order.buyerPhone && (
                <div className="text-md opacity-90">
                  Téléphone :
                  <span className="font-bold ml-1">
                    {formatPhoneNumber(order.buyerPhone)}
                  </span>
                </div>
              )}

              {order.deliveryAddress && (
                <div className="text-md opacity-90">
                  Adresse :
                  <span className="font-bold ml-1">
                    {order.deliveryAddress}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* =========================== */}
          {/*     TABLEAU DÉTAILS         */}
          {/* =========================== */}
          <div className="mb-4 border-b">
            <table className="w-full border border-base-300 rounded-lg overflow-hidden">
              <thead className="bg-base-200 text-sm">
                <tr>
                  <th className="p-3 text-left">Description</th>
                  <th className="p-3 text-center w-28">Quantité</th>
                  <th className="p-3 text-right w-32">Prix unitaire</th>
                  <th className="p-3 text-right w-32">Total</th>
                </tr>
              </thead>

              <tbody>
                {shippingGroups.map((group, groupIndex) => {
                  const { shipping, items } = group;
                  const isLastGroup = groupIndex === shippingGroups.length - 1;

                  return (
                    <React.Fragment key={groupIndex}>
                      {/* PRODUITS */}
                      {items.map((item) => (
                        <tr key={item.orderItemId}>
                          <td className="p-2 align-top">
                            <div>{item.productNameFr}</div>

                            {item.optionName && (
                              <div className="text-xs opacity-70 mt-1">
                                Option : {item.optionName}{" "}
                                {item.optionSurplus
                                  ? `(+${item.optionSurplus}$)`
                                  : ""}
                              </div>
                            )}
                          </td>

                          <td className="p-2 text-center align-top">
                            {item.quantity}
                          </td>
                          <td className="p-2 text-right align-top">
                            {item.unitPrice?.toFixed(2)} $
                          </td>
                          <td className="p-2 text-right align-top">
                            {item.totalPrice?.toFixed(2)} $
                          </td>
                        </tr>
                      ))}

                      {/* SHIPPING LINE */}
                      <tr>
                        <td className=" pt-0" colSpan={4}>
                          {shipping ? (
                            <>
                              <div
                                className="inline-block px-2 py-1 text-xs 
                                        rounded-md bg-blue-100 text-blue-700 
                                        border border-blue-300 mr-2 mb-1 "
                              >
                                Livraison
                              </div>
                              <span className="text-xs opacity-80 italic">
                                {shipping.carrier} — {shipping.serviceName} (
                                {shipping.amount.toFixed(2)} $)
                              </span>
                            </>
                          ) : (
                            <div
                              className="inline-block px-2 py-1 text-xs 
                                        rounded-md bg-ananas text-primary
                                       mr-2 mb-1 "
                            >
                              Vente directe
                            </div>
                          )}
                        </td>
                      </tr>

                      {/* SÉPARATEUR ENTRE GROUPES */}
                      {!isLastGroup && (
                        <tr>
                          <td colSpan={4}>
                            <hr className="border-t border-base-300 my-3" />
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* =========================== */}
          {/*        RÉSUMÉ               */}
          {/* =========================== */}
          <div className="flex justify-end mt-2">
            <div className="w-full max-w-xs space-y-1 text-sm">
              <Line
                label="Sous-total (produits)"
                value={`${order.providerProductSubTotal.toFixed(2)} $`}
              />

              <Line
                label="Livraison"
                value={`${order.providerShipping.toFixed(2)} $`}
              />

              <hr className="border-base-300 my-2" />

              <Line
                label="Sous-total final"
                value={`${order.providerSubTotalFinal.toFixed(2)} $`}
              />

              <Line
                label="TPS (5%)"
                value={`${order.providerTps.toFixed(2)} $`}
              />

              <Line
                label="TVQ (9.975%)"
                value={`${order.providerTvq.toFixed(2)} $`}
              />

              <Line
                label="Total"
                bold
                value={`${order.providerTotal.toFixed(2)} $`}
              />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Line({ label, value, bold }) {
  return (
    <div className="flex justify-between">
      <span className={bold ? "font-bold text-base" : "opacity-70"}>
        {label}
      </span>
      <span className={bold ? "font-bold text-base" : "font-medium"}>
        {value}
      </span>
    </div>
  );
}
